import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import type { ReactNode, RefObject } from 'react';
import { ChevronUp, ChevronDown, Search, X } from '../../icons';
import { tMemo, getMemoLang, subscribeMemoLang } from '../../lib/memo-i18n';
import type { MemoMatch } from './memo-find';

/**
 * MemoFindBar / MemoFindHighlight — 열어둔 메모 **안에서** 단어 찾기 (2026-08-18).
 *
 * 목록 검색(`.memo-search`)이 "어느 메모냐" 를 고르는 도구라면, 이쪽은 "그 메모 어디냐" 다.
 * 편집 화면 맨 위에 목록 검색과 같은 자리·같은 모양으로 둔다.
 *
 * ★ 하이라이트가 오버레이인 이유: 크로미움은 **포커스가 없는 textarea 의 선택 영역을 그리지
 *   않는다**(2026-08-18 실측). 찾기 입력에 커서를 둔 채로 일치를 보여주려면 선택만으로는
 *   안 되고, 본문과 같은 글꼴·폭으로 글을 한 벌 더 깔아 그 위에 표시해야 한다.
 *   textarea 는 배경이 투명(`.memo-ed-body`)이라 아래 깔린 이 층이 그대로 비친다.
 */

interface MemoFindBarProps {
  query: string;
  /** 현재 일치 순번(0-based). total 이 0 이면 의미 없음. */
  index: number;
  total: number;
  inputRef: RefObject<HTMLInputElement>;
  onQueryChange: (q: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onClear: () => void;
}

export function MemoFindBar({
  query, index, total, inputRef, onQueryChange, onNext, onPrev, onClear,
}: MemoFindBarProps) {
  useSyncExternalStore(subscribeMemoLang, getMemoLang, getMemoLang);

  const has = query.trim() !== '';
  const disabled = total === 0;

  return (
    <div className="memo-find">
      <Search size={13} className="memo-find-ico" aria-hidden />
      <input
        ref={inputRef}
        className="memo-find-input"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={tMemo('memo.find_placeholder')}
        aria-label={tMemo('memo.find_placeholder')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) onPrev(); else onNext();
            return;
          }
          // Esc — 검색어가 남아 있으면 그것만 지운다. 패널의 전역 Esc(닫기)까지 가지 않도록
          // 여기서 끊는다. 빈 상태에서의 Esc 는 그대로 흘려보내 패널이 닫히게 둔다.
          if (e.key === 'Escape' && has) {
            e.stopPropagation();
            onClear();
          }
        }}
      />

      <span className="memo-find-count" aria-live="polite">
        {has ? (total > 0 ? tMemo('memo.find_count', { n: index + 1, total }) : tMemo('memo.find_none')) : ''}
      </span>

      <button
        type="button"
        className="memo-find-nav"
        onClick={onPrev}
        disabled={disabled}
        title={tMemo('memo.find_prev')}
        aria-label={tMemo('memo.find_prev')}
      >
        <ChevronUp size={13} />
      </button>
      <button
        type="button"
        className="memo-find-nav"
        onClick={onNext}
        disabled={disabled}
        title={tMemo('memo.find_next')}
        aria-label={tMemo('memo.find_next')}
      >
        <ChevronDown size={13} />
      </button>
      {has && (
        <button
          type="button"
          className="memo-find-nav"
          onClick={onClear}
          title={tMemo('memo.find_clear')}
          aria-label={tMemo('memo.find_clear')}
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

interface MemoFindHighlightProps {
  body: string;
  matches: MemoMatch[];
  /** 현재 일치 순번 — 이것만 진하게 칠한다. */
  current: number;
  textareaRef: RefObject<HTMLTextAreaElement>;
}

export function MemoFindHighlight({ body, matches, current, textareaRef }: MemoFindHighlightProps) {
  const hlRef = useRef<HTMLDivElement | null>(null);
  const active = matches.length > 0;

  // 본문 스크롤·크기를 따라간다. 오버레이는 `overflow:hidden` 이라 스크롤바가 생기지 않지만
  // scrollTop 은 프로그램으로 옮길 수 있다 — 그 성질로 본문과 자리를 맞춘다.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const el = hlRef.current;
    if (!ta || !el) return;
    const sync = () => {
      el.style.width = `${ta.clientWidth}px`;
      el.scrollTop = ta.scrollTop;
    };
    sync();
    ta.addEventListener('scroll', sync);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    ro?.observe(ta);
    return () => {
      ta.removeEventListener('scroll', sync);
      ro?.disconnect();
    };
  }, [textareaRef, active]);

  // 본문·일치가 바뀐 직후에도 자리를 다시 맞춘다(리스너는 그대로 두고 값만).
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    const el = hlRef.current;
    if (!ta || !el) return;
    el.style.width = `${ta.clientWidth}px`;
    el.scrollTop = ta.scrollTop;
  });

  if (!active) return null;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) nodes.push(body.slice(cursor, m.start));
    nodes.push(
      <mark key={`${m.start}-${m.end}`} className={i === current ? 'is-current' : undefined}>
        {body.slice(m.start, m.end)}
      </mark>,
    );
    cursor = m.end;
  });
  nodes.push(body.slice(cursor));
  // 본문이 줄바꿈으로 끝나면 마지막 빈 줄에 내용이 없어 높이가 한 줄 모자란다 — 폭 0 글자로 채운다.
  nodes.push('​');

  return (
    <div ref={hlRef} className="memo-ed-hl" aria-hidden="true">
      {nodes}
    </div>
  );
}
